import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Ref,
  type ReactNode,
} from "react";
import {
  Button,
  Input,
  Label,
  Radio,
  RadioGroup,
  SearchField,
} from "react-aria-components";

import arrowDownIcon from "@/assets/figma/chat-add-menu/arrow-down.svg";
import arrowLeftIcon from "@/assets/figma/chat-add-menu/arrow-left.svg";
import arrowRightIcon from "@/assets/figma/chat-add-menu/arrow-right.svg";
import capabilityImageIcon from "@/assets/figma/chat-add-menu/capability-image.svg";
import capabilityImageMark from "@/assets/figma/chat-add-menu/capability-image-mark.svg";
import capabilityVideoIcon from "@/assets/figma/chat-add-menu/capability-video.svg";
import capabilityVideoMarkBottom from "@/assets/figma/chat-add-menu/capability-video-mark-bottom.svg";
import capabilityVideoMarkTop from "@/assets/figma/chat-add-menu/capability-video-mark-top.svg";
import categoryAllIcon from "@/assets/figma/chat-add-menu/library-all.svg";
import libraryAssetsFade from "@/assets/figma/chat-add-menu/library-assets-fade.svg";
import libraryCategoryFade from "@/assets/figma/chat-add-menu/library-category-fade.svg";
import documentGenericIcon from "@/assets/figma/chat-add-menu/library-document-generic.svg";
import documentHomeIcon from "@/assets/figma/chat-add-menu/library-document-home.svg";
import documentStackIcon from "@/assets/figma/chat-add-menu/library-document-stack.svg";
import categoryFileIcon from "@/assets/figma/chat-add-menu/library-file.svg";
import categoryImageIcon from "@/assets/figma/chat-add-menu/library-image.svg";
import categoryVideoIcon from "@/assets/figma/chat-add-menu/library-video-icon.svg";
import navCapabilityIcon from "@/assets/figma/chat-add-menu/nav-capability.svg";
import navCapabilitySelectedIcon from "@/assets/figma/chat-add-menu/nav-capability-selected.svg";
import navLibraryIcon from "@/assets/figma/chat-add-menu/nav-library.svg";
import navLibrarySelectedIcon from "@/assets/figma/chat-add-menu/nav-library-selected.svg";
import navToolsIcon from "@/assets/figma/chat-add-menu/nav-tools.svg";
import navToolsSelectedIcon from "@/assets/figma/chat-add-menu/nav-tools-selected.svg";
import navUploadIcon from "@/assets/figma/chat-add-menu/nav-upload.svg";
import searchIcon from "@/assets/figma/chat-add-menu/search.svg";
import { cn } from "@/shared/lib/cn";
import {
  MerryAssetHoverPreview,
  type MerryAssetHoverPreviewProps,
} from "@/shared/ui/MerryAssetHoverPreview";
import type { MerryChatExtensionViewModel } from "@/shared/ui/chatExtensionTypes";
import {
  defaultMerryImageParameters,
  defaultMerryVideoParameters,
  getMerryChatAddMenuFigmaNodeId,
  getMerryChatAddMenuSize,
  imageAspectRatioOptions,
  imageLongEdgeOptions,
  videoAspectRatioOptions,
  videoDurationOptions,
  videoResolutionOptions,
  videoSoundOptions,
  type MerryChatAddMenuView,
  type MerryImageParameters,
  type MerryVideoParameters,
} from "@/shared/ui/merryChatAddMenuVariants";

gsap.registerPlugin(useGSAP);

export interface MerryChatAddMenuTool {
  description: string;
  icon: string;
  id: string;
  label: string;
}

export interface MerryChatAddMenuCapability {
  description: readonly [string, string];
  id: string;
  kind: "image" | "video";
  label: string;
  previewImages: ReadonlyArray<string>;
}

export interface MerryChatAddMenuCategory {
  countLabel: string;
  id: string;
  kind: "all" | "file" | "image" | "video";
  label: string;
  previewImage?: string;
}

export type MerryChatAddMenuAsset =
  | {
      alt: string;
      id: string;
      image: string;
      kind: "image";
    }
  | {
      id: string;
      kind: "document";
      label: string;
      visual: "generic" | "home" | "stack";
    }
  | {
      id: string;
      kind: "resource";
      label: string;
      previewUrl?: string;
      resourceType: "file" | "image" | "video";
    };

export type MerryChatAddMenuAssetState =
  | "idle"
  | "loading"
  | "ready"
  | "error";

interface ActiveAssetPreview
  extends Pick<
    MerryAssetHoverPreviewProps,
    "alt" | "anchor" | "host" | "src"
  > {
  assetId: string;
}

export interface MerryChatAddMenuCatalog {
  assets: ReadonlyArray<MerryChatAddMenuAsset>;
  capabilities: ReadonlyArray<MerryChatAddMenuCapability>;
  categories: ReadonlyArray<MerryChatAddMenuCategory>;
  tools: ReadonlyArray<MerryChatAddMenuTool>;
}

export interface MerryChatAddMenuProps {
  "aria-label"?: string;
  "data-testid"?: string;
  activeCategoryId?: string;
  assetStateMessage?: string;
  assetState?: MerryChatAddMenuAssetState;
  catalog: MerryChatAddMenuCatalog;
  className?: string;
  defaultView?: MerryChatAddMenuView;
  imageParameters?: MerryImageParameters;
  navigationMode?: "all" | "resources-only";
  onAssetSelect?: (asset: MerryChatAddMenuAsset) => void;
  onCategorySelect?: (category: MerryChatAddMenuCategory) => void;
  onCapabilitySelect?: (capability: MerryChatAddMenuCapability) => void;
  onImageParametersChange?: (parameters: MerryImageParameters) => void;
  onMoreRatios?: () => void;
  onRetryAssets?: () => void;
  onToolSelect?: (tool: MerryChatAddMenuTool) => void;
  onUpload?: () => void;
  onVideoParametersChange?: (parameters: MerryVideoParameters) => void;
  onViewChange?: (view: MerryChatAddMenuView) => void;
  onOverflowExtensionSelect?: (extensionId: string) => void;
  overflowExtensions?: ReadonlyArray<MerryChatExtensionViewModel>;
  selectedAssetIds?: ReadonlyArray<string>;
  videoParameters?: MerryVideoParameters;
  view?: MerryChatAddMenuView;
}

const merryNavItems = [
  {
    activeIcon: navCapabilitySelectedIcon,
    icon: navCapabilityIcon,
    label: "能力",
    view: "capabilities",
  },
  {
    activeIcon: navToolsSelectedIcon,
    icon: navToolsIcon,
    label: "工具",
    view: "tools",
  },
] as const satisfies ReadonlyArray<{
  activeIcon: string;
  icon: string;
  label: string;
  view: MerryChatAddMenuView;
}>;

const referenceNavItems = [
  {
    activeIcon: navLibrarySelectedIcon,
    icon: navLibraryIcon,
    label: "来自库",
    view: "library-categories",
  },
] as const satisfies ReadonlyArray<{
  activeIcon: string;
  icon: string;
  label: string;
  view: MerryChatAddMenuView;
}>;

const categoryIcons = {
  all: categoryAllIcon,
  file: categoryFileIcon,
  image: categoryImageIcon,
  video: categoryVideoIcon,
} as const;

const documentIcons = {
  generic: documentGenericIcon,
  home: documentHomeIcon,
  stack: documentStackIcon,
} as const;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isParameterView(view: MerryChatAddMenuView) {
  return view === "video-parameters" || view === "image-parameters";
}

function OverflowExtensionsPanel({
  extensions,
  onSelect,
}: {
  extensions: ReadonlyArray<MerryChatExtensionViewModel>;
  onSelect?: (extensionId: string) => void;
}) {
  return (
    <div
      aria-label="更多已添加扩展"
      className="merry-chat-add-menu__overflow-extensions"
      role="list"
    >
      {extensions.map((extension) => (
        <MenuButton
          className="merry-chat-add-menu__overflow-extension"
          key={extension.id}
          onPress={() => onSelect?.(extension.id)}
        >
          <img alt="" aria-hidden="true" src={extension.icon} />
          <span>
            <strong>{extension.label}</strong>
            <span>
              {extension.summary ??
                (extension.configurable ? "点击调整生成参数" : "已添加到对话")}
            </span>
          </span>
        </MenuButton>
      ))}
    </div>
  );
}

function isResolution(value: string): value is MerryVideoParameters["resolution"] {
  return videoResolutionOptions.some((option) => option === value);
}

function isVideoAspectRatio(
  value: string,
): value is MerryVideoParameters["aspectRatio"] {
  return videoAspectRatioOptions.some((option) => option.value === value);
}

function isImageAspectRatio(
  value: string,
): value is MerryImageParameters["aspectRatio"] {
  return imageAspectRatioOptions.some((option) => option.value === value);
}

function isVideoDuration(
  value: number,
): value is MerryVideoParameters["duration"] {
  return videoDurationOptions.some((option) => option === value);
}

function isImageLongEdge(
  value: number,
): value is MerryImageParameters["longEdge"] {
  return imageLongEdgeOptions.some((option) => option === value);
}

function MenuButton({
  ariaPressed,
  buttonRef,
  children,
  className,
  isDisabled,
  onFocusChange,
  onHoverChange,
  onPress,
}: {
  ariaPressed?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  children: ReactNode;
  className?: string;
  isDisabled?: boolean;
  onFocusChange?: (isFocused: boolean) => void;
  onHoverChange?: (isHovered: boolean) => void;
  onPress?: () => void;
}) {
  return (
    <Button
      className={({ isFocusVisible, isHovered, isPressed }) =>
        cn(
          "merry-chat-add-menu__button",
          isHovered && "merry-chat-add-menu__button--hovered",
          isPressed && "merry-chat-add-menu__button--pressed",
          isFocusVisible && "merry-chat-add-menu__button--focus-visible",
          className,
        )
      }
      aria-pressed={ariaPressed}
      isDisabled={isDisabled}
      onFocusChange={onFocusChange}
      onHoverChange={onHoverChange}
      onPress={onPress}
      ref={buttonRef}
    >
      {children}
    </Button>
  );
}

interface MenuNavigationProps {
  activeView: MerryChatAddMenuView;
  mode: "all" | "resources-only";
  onUpload?: () => void;
  onViewChange: (view: MerryChatAddMenuView) => void;
}

function MenuNavigation({
  activeView,
  mode,
  onUpload,
  onViewChange,
}: MenuNavigationProps) {
  const renderNavItem = (
    item: (typeof merryNavItems)[number] | (typeof referenceNavItems)[number],
  ) => {
    const isSelected =
      activeView === item.view ||
      (item.view === "library-categories" && activeView === "library-assets");

    return (
      <MenuButton
        className={cn(
          "merry-chat-add-menu__nav-item",
          isSelected && "merry-chat-add-menu__nav-item--selected",
        )}
        key={item.view}
        onPress={() => onViewChange(item.view)}
      >
        <span className="merry-chat-add-menu__nav-content">
          <img
            alt=""
            aria-hidden="true"
            src={isSelected ? item.activeIcon : item.icon}
          />
          <span>{item.label}</span>
        </span>
        {isSelected ? null : (
          <img
            alt=""
            aria-hidden="true"
            className="merry-chat-add-menu__nav-arrow"
            src={arrowRightIcon}
          />
        )}
      </MenuButton>
    );
  };

  return (
    <nav aria-label="添加内容分类" className="merry-chat-add-menu__navigation">
      <span className="merry-chat-add-menu__nav-group">
        <span className="merry-chat-add-menu__nav-title">Merry</span>
        {mode === "all" ? merryNavItems.map(renderNavItem) : null}
      </span>
      <span className="merry-chat-add-menu__nav-group">
        <span className="merry-chat-add-menu__nav-title">引用</span>
        {referenceNavItems.map(renderNavItem)}
        {onUpload ? (
          <MenuButton
            className="merry-chat-add-menu__nav-item"
            onPress={onUpload}
          >
            <span className="merry-chat-add-menu__nav-content">
              <img alt="" aria-hidden="true" src={navUploadIcon} />
              <span>上传</span>
            </span>
          </MenuButton>
        ) : null}
      </span>
    </nav>
  );
}

function ToolsPanel({
  onToolSelect,
  tools,
}: {
  onToolSelect?: (tool: MerryChatAddMenuTool) => void;
  tools: ReadonlyArray<MerryChatAddMenuTool>;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
  const filteredTools = normalizedQuery
    ? tools.filter((tool) =>
        `${tool.label}${tool.description}`
          .toLocaleLowerCase("zh-CN")
          .includes(normalizedQuery),
      )
    : tools;

  return (
    <div className="merry-chat-add-menu__tools">
      <SearchField
        aria-label="搜索工具"
        className="merry-chat-add-menu__search"
        onChange={setQuery}
        value={query}
      >
        <img alt="" aria-hidden="true" src={searchIcon} />
        <Input placeholder="搜索组件的快捷能力" />
      </SearchField>
      <div className="merry-chat-add-menu__tool-list">
        {filteredTools.map((tool) => (
          <MenuButton
            className="merry-chat-add-menu__tool"
            key={tool.id}
            onPress={() => onToolSelect?.(tool)}
          >
            <img
              alt=""
              aria-hidden="true"
              className="merry-chat-add-menu__tool-icon"
              src={tool.icon}
            />
            <span className="merry-chat-add-menu__tool-copy">
              <strong>{tool.label}</strong>
              <span>{tool.description}</span>
            </span>
          </MenuButton>
        ))}
        {filteredTools.length === 0 ? (
          <p className="merry-chat-add-menu__empty">没有匹配的工具</p>
        ) : null}
      </div>
    </div>
  );
}

function CapabilityPreview({
  capability,
}: {
  capability: MerryChatAddMenuCapability;
}) {
  if (capability.kind === "video") {
    return (
      <span className="merry-chat-add-menu__capability-preview merry-chat-add-menu__capability-preview--video">
        {(["rear", "side", "front"] as const).map((layer) => (
          <span
            className={cn(
              "merry-chat-add-menu__capability-media-layer",
              `merry-chat-add-menu__capability-media-layer--video-${layer}`,
            )}
            key={layer}
          >
            <span className="merry-chat-add-menu__capability-media-card">
              <img alt="" src={capability.previewImages[0]} />
            </span>
          </span>
        ))}
        <img
          alt=""
          className="merry-chat-add-menu__video-mark merry-chat-add-menu__video-mark--top"
          src={capabilityVideoMarkTop}
        />
        <img
          alt=""
          className="merry-chat-add-menu__video-mark merry-chat-add-menu__video-mark--bottom"
          src={capabilityVideoMarkBottom}
        />
      </span>
    );
  }

  return (
    <span className="merry-chat-add-menu__capability-preview merry-chat-add-menu__capability-preview--image">
      {(["rear", "side", "front"] as const).map((layer, index) => (
        <span
          className={cn(
            "merry-chat-add-menu__capability-media-layer",
            `merry-chat-add-menu__capability-media-layer--image-${layer}`,
          )}
          key={layer}
        >
          <span className="merry-chat-add-menu__capability-media-card">
            <img alt="" src={capability.previewImages[index]} />
          </span>
        </span>
      ))}
      <img alt="" className="merry-chat-add-menu__image-mark" src={capabilityImageMark} />
    </span>
  );
}

function CapabilitiesPanel({
  capabilities,
  onCapabilitySelect,
}: {
  capabilities: ReadonlyArray<MerryChatAddMenuCapability>;
  onCapabilitySelect: (capability: MerryChatAddMenuCapability) => void;
}) {
  return (
    <div className="merry-chat-add-menu__capabilities">
      {capabilities.map((capability) => (
        <MenuButton
          className="merry-chat-add-menu__capability"
          key={capability.id}
          onPress={() => onCapabilitySelect(capability)}
        >
          <span className="merry-chat-add-menu__capability-heading">
            <img
              alt=""
              src={
                capability.kind === "video"
                  ? capabilityVideoIcon
                  : capabilityImageIcon
              }
            />
            <strong>{capability.label}</strong>
          </span>
          <span className="merry-chat-add-menu__capability-description">
            {capability.description.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
          <CapabilityPreview capability={capability} />
        </MenuButton>
      ))}
    </div>
  );
}

function CategoriesPanel({
  categories,
  onCategorySelect,
}: {
  categories: ReadonlyArray<MerryChatAddMenuCategory>;
  onCategorySelect: (category: MerryChatAddMenuCategory) => void;
}) {
  return (
    <div className="merry-chat-add-menu__categories">
      {categories.map((category) => (
        <MenuButton
          className="merry-chat-add-menu__category"
          key={category.id}
          onPress={() => onCategorySelect(category)}
        >
          <span className="merry-chat-add-menu__category-preview merry-chat-add-menu__category-preview--back">
            <img alt="" src={documentGenericIcon} />
            <span />
            <span />
          </span>
          <span className="merry-chat-add-menu__category-preview merry-chat-add-menu__category-preview--front">
            {category.previewImage ? (
              <img alt="" src={category.previewImage} />
            ) : (
              <span className="merry-chat-add-menu__category-document">
                <img alt="" src={documentIcons.home} />
                <span>家居收纳达人短视频起量+广告承接·大促备货中</span>
              </span>
            )}
          </span>
          <img
            alt=""
            aria-hidden="true"
            className="merry-chat-add-menu__category-fade"
            src={libraryCategoryFade}
          />
          <span className="merry-chat-add-menu__category-copy">
            <span className="merry-chat-add-menu__category-title">
              <span className="merry-chat-add-menu__category-icon">
                <img alt="" src={categoryIcons[category.kind]} />
              </span>
              <strong>{category.label}</strong>
            </span>
            <span>{category.countLabel}</span>
          </span>
        </MenuButton>
      ))}
    </div>
  );
}

function AssetTile({
  asset,
  isSelected,
  onPress,
  onPreviewChange,
}: {
  asset: MerryChatAddMenuAsset;
  isSelected: boolean;
  onPress: () => void;
  onPreviewChange: (
    assetId: string,
    preview: ActiveAssetPreview | null,
  ) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isFocusedRef = useRef(false);
  const isHoveredRef = useRef(false);
  const resourceIcon =
    asset.kind === "resource"
      ? categoryIcons[asset.resourceType]
      : documentGenericIcon;
  const previewAlt =
    asset.kind === "image"
      ? asset.alt
      : asset.kind === "resource" && asset.previewUrl
        ? asset.label
        : "";
  const previewSrc =
    asset.kind === "image"
      ? asset.image
      : asset.kind === "resource" && asset.previewUrl
        ? asset.previewUrl
        : "";

  const syncPreview = useCallback(() => {
    const anchor = anchorRef.current;
    const host = anchor?.closest<HTMLElement>(".merry-chat-add-menu");
    if (
      previewSrc &&
      anchor &&
      host &&
      (isHoveredRef.current || isFocusedRef.current)
    ) {
      onPreviewChange(asset.id, {
        alt: previewAlt,
        anchor,
        assetId: asset.id,
        host,
        src: previewSrc,
      });
      return;
    }

    onPreviewChange(asset.id, null);
  }, [asset.id, onPreviewChange, previewAlt, previewSrc]);

  useEffect(
    () => () => onPreviewChange(asset.id, null),
    [asset.id, onPreviewChange],
  );

  return (
    <MenuButton
      ariaPressed={isSelected}
      buttonRef={anchorRef}
      className={cn(
        "merry-chat-add-menu__asset",
        isSelected && "merry-chat-add-menu__asset--selected",
      )}
      onFocusChange={(isFocused) => {
        isFocusedRef.current = isFocused;
        syncPreview();
      }}
      onHoverChange={(isHovered) => {
        isHoveredRef.current = isHovered;
        syncPreview();
      }}
      onPress={() => {
        onPreviewChange(asset.id, null);
        onPress();
      }}
    >
      {asset.kind === "image" ? (
        <img alt={asset.alt} className="merry-chat-add-menu__asset-image" src={asset.image} />
      ) : asset.kind === "resource" && asset.previewUrl ? (
        <img
          alt={asset.label}
          className="merry-chat-add-menu__asset-image"
          src={asset.previewUrl}
        />
      ) : (
        <span className="merry-chat-add-menu__document">
          <img
            alt=""
            className="merry-chat-add-menu__document-icon"
            src={asset.kind === "document" ? documentIcons[asset.visual] : resourceIcon}
          />
          <span>{asset.label}</span>
        </span>
      )}
    </MenuButton>
  );
}

function AssetsPanel({
  allCountLabel,
  assetState,
  assetStateMessage,
  assets,
  categoryLabel,
  onAssetSelect,
  onBack,
  onPreviewChange,
  onRetry,
  selectedAssetIds,
}: {
  allCountLabel: string;
  assetState: MerryChatAddMenuAssetState;
  assetStateMessage?: string;
  assets: ReadonlyArray<MerryChatAddMenuAsset>;
  categoryLabel: string;
  onAssetSelect?: (asset: MerryChatAddMenuAsset) => void;
  onBack: () => void;
  onPreviewChange: (
    assetId: string,
    preview: ActiveAssetPreview | null,
  ) => void;
  onRetry?: () => void;
  selectedAssetIds: ReadonlySet<string>;
}) {
  const showState = assetState !== "ready" || assets.length === 0;
  const stateMessage =
    assetStateMessage ??
    (assetState === "loading" || assetState === "idle"
      ? "正在读取云盘文件…"
      : assetState === "error"
        ? "云盘文件暂时无法读取"
        : "当前分类暂无文件");

  return (
    <div className="merry-chat-add-menu__assets-panel">
      {showState ? (
        <div className="merry-chat-add-menu__asset-state" role={assetState === "error" ? "alert" : "status"}>
          <span>{stateMessage}</span>
          {assetState === "error" && onRetry ? (
            <MenuButton className="merry-chat-add-menu__asset-retry" onPress={onRetry}>
              重新读取
            </MenuButton>
          ) : null}
        </div>
      ) : (
        <div className="merry-chat-add-menu__asset-grid">
          {assets.map((asset) => (
            <AssetTile
              asset={asset}
              isSelected={selectedAssetIds.has(asset.id)}
              key={asset.id}
              onPress={() => onAssetSelect?.(asset)}
              onPreviewChange={onPreviewChange}
            />
          ))}
        </div>
      )}
      <img
        alt=""
        aria-hidden="true"
        className="merry-chat-add-menu__assets-fade"
        src={libraryAssetsFade}
      />
      <MenuButton className="merry-chat-add-menu__back" onPress={onBack}>
        <span className="merry-chat-add-menu__back-title">
          <img alt="" src={arrowLeftIcon} />
          <strong>{categoryLabel}</strong>
        </span>
        <span>{allCountLabel}</span>
      </MenuButton>
    </div>
  );
}

interface ParameterGroupProps {
  children: ReactNode;
  label: string;
  onChange: (value: string) => void;
  value: string;
}

function ParameterGroup({
  children,
  label,
  onChange,
  value,
}: ParameterGroupProps) {
  return (
    <RadioGroup className="merry-chat-add-menu__parameter-group" onChange={onChange} value={value}>
      <Label className="merry-chat-add-menu__parameter-label">{label}</Label>
      <span className="merry-chat-add-menu__parameter-options">{children}</span>
    </RadioGroup>
  );
}

function ParameterRadio({ children, value }: { children: ReactNode; value: string }) {
  return (
    <Radio
      className={({ isFocusVisible, isHovered, isPressed, isSelected }) =>
        cn(
          "merry-chat-add-menu__parameter-radio",
          isSelected && "merry-chat-add-menu__parameter-radio--selected",
          isHovered && "merry-chat-add-menu__parameter-radio--hovered",
          isPressed && "merry-chat-add-menu__parameter-radio--pressed",
          isFocusVisible && "merry-chat-add-menu__button--focus-visible",
        )
      }
      value={value}
    >
      {children}
    </Radio>
  );
}

function getResolutionLabel(value: MerryVideoParameters["resolution"]) {
  return value.replace(/(P|K)$/, " $1");
}

function VideoParametersPanel({
  onChange,
  parameters,
}: {
  onChange: (parameters: MerryVideoParameters) => void;
  parameters: MerryVideoParameters;
}) {
  return (
    <div className="merry-chat-add-menu__parameters">
      <ParameterGroup
        label="画面比例"
        onChange={(value) => {
          if (isVideoAspectRatio(value)) onChange({ ...parameters, aspectRatio: value });
        }}
        value={parameters.aspectRatio}
      >
        {videoAspectRatioOptions.map((option) => (
          <ParameterRadio key={option.value} value={option.value}>
            {option.label}
          </ParameterRadio>
        ))}
      </ParameterGroup>
      <ParameterGroup
        label="分辨率"
        onChange={(value) => {
          if (isResolution(value)) onChange({ ...parameters, resolution: value });
        }}
        value={parameters.resolution}
      >
        {videoResolutionOptions.map((option) => (
          <ParameterRadio key={option} value={option}>
            {getResolutionLabel(option)}
          </ParameterRadio>
        ))}
      </ParameterGroup>
      <ParameterGroup
        label="视频时长"
        onChange={(value) => {
          const duration = Number(value);
          if (isVideoDuration(duration)) {
            onChange({ ...parameters, duration });
          }
        }}
        value={String(parameters.duration)}
      >
        {videoDurationOptions.map((option) => (
          <ParameterRadio key={option} value={String(option)}>
            {option} 秒
          </ParameterRadio>
        ))}
      </ParameterGroup>
      <ParameterGroup
        label="声音"
        onChange={(value) => {
          if (value === "with-sound" || value === "muted") {
            onChange({ ...parameters, sound: value });
          }
        }}
        value={parameters.sound}
      >
        {videoSoundOptions.map((option) => (
          <ParameterRadio key={option} value={option}>
            {option === "with-sound" ? "有声" : "无声"}
          </ParameterRadio>
        ))}
      </ParameterGroup>
    </div>
  );
}

function ImageParametersPanel({
  onChange,
  onMoreRatios,
  parameters,
}: {
  onChange: (parameters: MerryImageParameters) => void;
  onMoreRatios?: () => void;
  parameters: MerryImageParameters;
}) {
  return (
    <div className="merry-chat-add-menu__parameters">
      <ParameterGroup
        label="画面比例"
        onChange={(value) => {
          if (isImageAspectRatio(value)) onChange({ ...parameters, aspectRatio: value });
        }}
        value={parameters.aspectRatio}
      >
        {imageAspectRatioOptions.map((option) => (
          <ParameterRadio key={option.value} value={option.value}>
            {option.label}
          </ParameterRadio>
        ))}
        <MenuButton className="merry-chat-add-menu__more-ratios" onPress={onMoreRatios}>
          <img alt="" src={arrowDownIcon} />
          <span>更多</span>
        </MenuButton>
      </ParameterGroup>
      <ParameterGroup
        label="输出尺寸"
        onChange={(value) => {
          const longEdge = Number(value);
          if (isImageLongEdge(longEdge)) onChange({ ...parameters, longEdge });
        }}
        value={String(parameters.longEdge)}
      >
        {imageLongEdgeOptions.map((option) => (
          <ParameterRadio key={option} value={String(option)}>
            长边 {option} px
          </ParameterRadio>
        ))}
      </ParameterGroup>
    </div>
  );
}

export function MerryChatAddMenu({
  "aria-label": ariaLabel = "添加内容菜单",
  "data-testid": testId,
  activeCategoryId = "all",
  assetState = "ready",
  assetStateMessage,
  catalog,
  className,
  defaultView = "default",
  imageParameters,
  navigationMode = "all",
  onAssetSelect,
  onCategorySelect,
  onCapabilitySelect,
  onImageParametersChange,
  onMoreRatios,
  onOverflowExtensionSelect,
  onRetryAssets,
  onToolSelect,
  onUpload,
  onVideoParametersChange,
  onViewChange,
  overflowExtensions = [],
  selectedAssetIds = [],
  videoParameters,
  view,
}: MerryChatAddMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isInitialMotionRef = useRef(true);
  const isInitialContentMotionRef = useRef(true);
  const [initialSize] = useState(() =>
    getMerryChatAddMenuSize(view ?? defaultView),
  );
  const [internalView, setInternalView] = useState(defaultView);
  const [activeAssetPreview, setActiveAssetPreview] =
    useState<ActiveAssetPreview | null>(null);
  const [renderedView, setRenderedView] = useState(view ?? defaultView);
  const [internalVideoParameters, setInternalVideoParameters] = useState(
    defaultMerryVideoParameters,
  );
  const [internalImageParameters, setInternalImageParameters] = useState(
    defaultMerryImageParameters,
  );
  const requestedView = view ?? internalView;
  const requestedSize = getMerryChatAddMenuSize(requestedView);
  const activeVideoParameters = videoParameters ?? internalVideoParameters;
  const activeImageParameters = imageParameters ?? internalImageParameters;
  const selectedAssetIdSet = new Set(selectedAssetIds);
  const activeCategory =
    catalog.categories.find((category) => category.id === activeCategoryId) ??
    catalog.categories.find((category) => category.kind === "all");

  const changeView = useCallback(
    (nextView: MerryChatAddMenuView) => {
      if (view === undefined) setInternalView(nextView);
      onViewChange?.(nextView);
    },
    [onViewChange, view],
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;
      if (!root || !content) return;

      gsap.killTweensOf([root, content]);

      if (isInitialMotionRef.current) {
        isInitialMotionRef.current = false;
        root.dataset.chatAddMenuAnimating = "false";
        return;
      }

      if (prefersReducedMotion()) {
        setRenderedView(requestedView);
        gsap.set(root, {
          height: requestedSize.height,
          width: requestedSize.width,
        });
        gsap.set(content, { autoAlpha: 1 });
        root.dataset.chatAddMenuAnimating = "false";
        return;
      }

      root.dataset.chatAddMenuAnimating = "true";
      gsap.to(root, {
        duration: 0.32,
        ease: "power3.inOut",
        height: requestedSize.height,
        overwrite: true,
        width: requestedSize.width,
        onComplete: () => {
          root.dataset.chatAddMenuAnimating = "false";
        },
        onInterrupt: () => {
          root.dataset.chatAddMenuAnimating = "false";
        },
      });
      gsap.to(content, {
        autoAlpha: 0,
        duration: 0.14,
        ease: "power2.in",
        overwrite: true,
        onComplete: () => setRenderedView(requestedView),
      });
    },
    { dependencies: [requestedView], scope: rootRef },
  );

  useGSAP(
    () => {
      const content = contentRef.current;
      if (!content) return;

      if (isInitialContentMotionRef.current) {
        isInitialContentMotionRef.current = false;
        return;
      }

      if (prefersReducedMotion()) {
        gsap.set(content, { autoAlpha: 1 });
        return;
      }

      gsap.fromTo(
        content,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.18,
          ease: "power2.out",
          overwrite: true,
          onComplete: () =>
            gsap.set(content, { clearProps: "opacity,visibility" }),
        },
      );
    },
    { dependencies: [renderedView], scope: rootRef },
  );

  const updateVideoParameters = (parameters: MerryVideoParameters) => {
    if (videoParameters === undefined) setInternalVideoParameters(parameters);
    onVideoParametersChange?.(parameters);
  };

  const updateImageParameters = (parameters: MerryImageParameters) => {
    if (imageParameters === undefined) setInternalImageParameters(parameters);
    onImageParametersChange?.(parameters);
  };

  const showNavigation =
    !isParameterView(renderedView) && renderedView !== "extension-overflow";

  const updateAssetPreview = useCallback(
    (assetId: string, preview: ActiveAssetPreview | null) => {
      setActiveAssetPreview((current) => {
        if (preview) return preview;
        return current?.assetId === assetId ? null : current;
      });
    },
    [],
  );

  return (
    <div
      aria-label={ariaLabel}
      className={cn("merry-chat-add-menu", className)}
      data-chat-add-menu-view={requestedView}
      data-figma-component-id="434:44644"
      data-figma-node-id={getMerryChatAddMenuFigmaNodeId(requestedView)}
      data-testid={testId}
      ref={rootRef}
      role="group"
      style={{
        height: initialSize.height,
        width: initialSize.width,
      }}
    >
      <span aria-hidden="true" className="merry-chat-add-menu__glass" />
      <div className="merry-chat-add-menu__layout" ref={contentRef}>
        {showNavigation ? (
          <MenuNavigation
            activeView={renderedView}
            mode={navigationMode}
            onUpload={onUpload}
            onViewChange={changeView}
          />
        ) : null}
        {renderedView === "tools" ? (
          <ToolsPanel onToolSelect={onToolSelect} tools={catalog.tools} />
        ) : null}
        {renderedView === "capabilities" ? (
          <CapabilitiesPanel
            capabilities={catalog.capabilities}
            onCapabilitySelect={(capability) => {
              onCapabilitySelect?.(capability);
              changeView(
                capability.kind === "video"
                  ? "video-parameters"
                  : "image-parameters",
              );
            }}
          />
        ) : null}
        {renderedView === "library-categories" ? (
          <CategoriesPanel
            categories={catalog.categories}
            onCategorySelect={(category) => {
              onCategorySelect?.(category);
              changeView("library-assets");
            }}
          />
        ) : null}
        {renderedView === "library-assets" ? (
          <AssetsPanel
            allCountLabel={
              activeCategory?.countLabel ?? ""
            }
            assetState={assetState}
            assetStateMessage={assetStateMessage}
            assets={catalog.assets}
            categoryLabel={activeCategory?.label ?? "全部"}
            onAssetSelect={onAssetSelect}
            onBack={() => changeView("library-categories")}
            onPreviewChange={updateAssetPreview}
            onRetry={onRetryAssets}
            selectedAssetIds={selectedAssetIdSet}
          />
        ) : null}
        {renderedView === "video-parameters" ? (
          <VideoParametersPanel
            onChange={updateVideoParameters}
            parameters={activeVideoParameters}
          />
        ) : null}
        {renderedView === "image-parameters" ? (
          <ImageParametersPanel
            onChange={updateImageParameters}
            onMoreRatios={onMoreRatios}
            parameters={activeImageParameters}
          />
        ) : null}
        {renderedView === "extension-overflow" ? (
          <OverflowExtensionsPanel
            extensions={overflowExtensions}
            onSelect={onOverflowExtensionSelect}
          />
        ) : null}
      </div>
      {renderedView === "library-assets" && activeAssetPreview ? (
        <MerryAssetHoverPreview
          alt={activeAssetPreview.alt}
          anchor={activeAssetPreview.anchor}
          host={activeAssetPreview.host}
          key={activeAssetPreview.assetId}
          src={activeAssetPreview.src}
        />
      ) : null}
      <span aria-hidden="true" className="merry-chat-add-menu__stroke" />
    </div>
  );
}
